import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getFamBySlug } from "../../../lib/api";
import { getCurrentUser } from "../../../lib/auth";
import { formatUsd } from "../../../lib/bankroll";
import { ConsensusBar } from "../../../components/ConsensusBar";
import { RoleBadge } from "../../../components/RoleBadge";
import { useFamChannel } from "../../../lib/realtime";
import { isMarketUnavailable, useQuotes, type Quote } from "../../../lib/api/market";
import { useFamTrophies } from "../../../lib/api/fantasy-fams";
import { TrophyBadge } from "../../../components/TrophyBadge";
import type { FamRole } from "../../../lib/types";

// Roles that may propose binding ideas. Mirrors role_caps.go on the Go side:
// PM, RM, Trader, Analyst can propose; Member cannot.
const PROPOSER_ROLES: FamRole[] = ["pm", "rm", "trader", "analyst"];

// /fam/:slug — Phase 1 read-only Fam viewer. Renders the composite, the
// member list with role badges, and the open ideas list. NO vote UI — Phase
// 2 wires up the proposal/vote interactions.
export function FamViewerRoute() {
	const { slug } = useParams<{ slug: string }>();
	const [currentUserId, setCurrentUserId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		void getCurrentUser().then((u) => {
			if (!cancelled) setCurrentUserId(u?.id ?? null);
		});
		return () => {
			cancelled = true;
		};
	}, []);

	const famQuery = useQuery({
		queryKey: ["fam", slug],
		queryFn: () => {
			if (!slug) throw new Error("missing slug");
			return getFamBySlug(slug);
		},
		enabled: !!slug,
	});

	// Live updates — opens a WebSocket to /api/fams/:slug/subscribe and
	// invalidates the relevant TanStack Query caches on inbound events.
	// Ref-counted internally so the IdeaDetailRoute child can re-use this
	// connection rather than opening its own.
	useFamChannel(slug);

	// Composite symbols are the keys of fam.composite. We pull batch quotes
	// for ALL of them up-front so each row + the weighted-stat banner share a
	// single query. Empty-list-safe: useQuotes() short-circuits when no
	// symbols are supplied (enabled:false on the underlying useQuery).
	const compositeSymbols = useMemo(
		() => (famQuery.data ? Object.keys(famQuery.data.composite) : []),
		[famQuery.data],
	);
	const quotesQuery = useQuotes(compositeSymbols);
	const quotesUnavailable = isMarketUnavailable(quotesQuery.error);

	if (famQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading Fam…
			</div>
		);
	}

	if (famQuery.isError || !famQuery.data) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load Fam.{" "}
					{famQuery.error instanceof Error ? famQuery.error.message : ""}
				</p>
				<Link
					to="/"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to dashboard
				</Link>
			</div>
		);
	}

	const fam = famQuery.data;
	const compositeEntries = Object.entries(fam.composite).sort((a, b) => b[1] - a[1]);
	const myRole = currentUserId
		? fam.members.find((m) => m.userId === currentUserId)?.role
		: undefined;
	const canPropose = myRole !== undefined && PROPOSER_ROLES.includes(myRole);
	const isPM = myRole === "pm";

	// Allocation-weighted day-change across the composite. This is what a PM
	// glances at: "is my book up today?" Skipped (null) if quotes are
	// unavailable or we don't have a quote for every symbol yet.
	const quotesByKey = quotesQuery.data?.quotes ?? {};
	const weightedDayChange = computeWeightedDayChange(compositeEntries, quotesByKey);

	return (
		<div className="mx-auto max-w-5xl space-y-8 px-4 py-8">
			<header className="flex items-center justify-between gap-4">
				<div>
					<Link
						to="/"
						className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
					>
						← Dashboard
					</Link>
					<h1 className="mt-1 text-2xl font-bold">{fam.name}</h1>
					<p className="text-sm text-[var(--color-muted)]">
						{fam.members.length}/{fam.maxMembers} members ·{" "}
						{fam.tier === "paid" ? "Paid" : "Free"} tier ·{" "}
						{formatUsd(fam.bankrollUsd)} bankroll
					</p>
				</div>
				{isPM && (
					<Link
						to={`/fam/${fam.slug}/settings`}
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
					>
						Settings
					</Link>
				)}
			</header>

			{/* Trophies — Fantasy Fams hardware. Rendered near the header so they
			    flex right next to the Fam name. Empty state nudges into opt-in. */}
			<TrophiesSection slug={fam.slug} />

			<section className="space-y-3">
				<div className="flex items-end justify-between">
					<div>
						<h2 className="text-lg font-semibold">Composite</h2>
						{quotesUnavailable ? (
							<p className="text-xs text-[var(--color-muted)]">
								Prices unavailable.
							</p>
						) : weightedDayChange !== null ? (
							<p className="text-xs">
								<span className="text-[var(--color-muted)]">
									Book today:{" "}
								</span>
								<span
									className={
										weightedDayChange >= 0
											? "text-[var(--color-accent)]"
											: "text-[var(--color-danger)]"
									}
								>
									{formatPctSigned(weightedDayChange)}
								</span>
							</p>
						) : compositeEntries.length > 0 ? (
							<p className="text-xs text-[var(--color-muted)]">
								Loading prices…
							</p>
						) : null}
					</div>
				</div>
				<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-2">
					{compositeEntries.length === 0 ? (
						<p className="p-3 text-sm text-[var(--color-muted)]">
							No positions yet. Propose an idea to add the first one.
						</p>
					) : (
						<ul>
							{compositeEntries.map(([symbol, allocation]) => {
								const q =
									quotesByKey[symbol] ??
									quotesByKey[bareSymbol(symbol)];
								return (
									<li key={symbol}>
										<Link
											to={`/stock/${encodeURIComponent(bareSymbol(symbol))}`}
											className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 rounded-md px-3 py-2 text-sm transition hover:bg-white/[0.02]"
										>
											<span className="font-mono font-semibold">
												{bareSymbol(symbol)}
											</span>
											<span className="font-mono tabular-nums text-[var(--color-muted)]">
												{quotesUnavailable
													? "—"
													: q
														? `$${q.last.toFixed(2)}`
														: "—"}
											</span>
											<span
												className={[
													"min-w-[64px] text-right font-mono tabular-nums",
													quotesUnavailable || !q
														? "text-[var(--color-muted)]"
														: q.changePct >= 0
															? "text-[var(--color-accent)]"
															: "text-[var(--color-danger)]",
												].join(" ")}
											>
												{quotesUnavailable || !q
													? "—"
													: formatPctSigned(q.changePct)}
											</span>
											<span className="min-w-[60px] text-right text-[var(--color-muted)]">
												{allocation.toFixed(2)}%
											</span>
										</Link>
									</li>
								);
							})}
						</ul>
					)}
				</div>
			</section>

			<section className="space-y-3">
				<h2 className="text-lg font-semibold">Members</h2>
				<div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
					<ul>
						{fam.members.map((m) => (
							<li
								key={m.userId}
								className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3 last:border-b-0"
							>
								<span className="text-sm">
									{m.xHandle ? `@${m.xHandle}` : m.userId.slice(0, 8)}
								</span>
								<RoleBadge role={m.role} />
							</li>
						))}
					</ul>
				</div>
			</section>

			<section className="space-y-3">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">Open ideas</h2>
					{canPropose && (
						<Link
							to={`/fam/${fam.slug}/propose`}
							className="rounded-md border border-[var(--color-accent)] bg-[var(--color-accent-soft)] px-3 py-1.5 text-sm font-semibold text-[var(--color-fg)] transition hover:opacity-90"
						>
							Propose idea
						</Link>
					)}
				</div>
				<div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
					{fam.openIdeas.length === 0 ? (
						<p className="p-4 text-sm text-[var(--color-muted)]">
							No open ideas.
						</p>
					) : (
						<ul>
							{fam.openIdeas.map((idea) => (
								<li key={idea.id}>
									<Link
										to={`/fam/${fam.slug}/idea/${idea.id}`}
										className="block border-b border-[var(--color-border)] px-4 py-3 transition hover:bg-white/[0.02] last:border-b-0"
									>
										<div className="flex items-center justify-between gap-3">
											<div>
												<p className="font-mono font-semibold">
													{idea.symbol} ·{" "}
													{idea.direction.toUpperCase()} ·{" "}
													{idea.allocationPct.toFixed(2)}%
												</p>
												<p className="mt-1 text-xs text-[var(--color-muted)]">
													{idea.thesis}
												</p>
											</div>
											<RoleBadge role={idea.proposerRole} />
										</div>
										<div className="mt-3">
											<ConsensusBar
												consensus={idea.consensus}
												thresholdPct={idea.thresholdPct}
												compact
											/>
										</div>
									</Link>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Simulated paper trade. Not investment advice. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}

// --- Trophies section -------------------------------------------------------
//
// Inline component (not a top-level export) because it's only used here. We
// scope the trophy query off the slug so it lives in the standard
// fantasy-fams query cache and invalidates with the rest of the Fantasy Fams
// surface (e.g. after a duel settle).

function TrophiesSection({ slug }: { slug: string }) {
	const trophies = useFamTrophies(slug);

	if (trophies.isLoading) {
		return (
			<section className="text-xs text-[var(--color-muted)]">
				Loading trophies…
			</section>
		);
	}

	if (trophies.isError) {
		// Trophy loading errors are non-fatal — render a quiet hint so the
		// rest of the Fam page stays usable.
		return null;
	}

	const list = trophies.data?.trophies ?? [];

	if (list.length === 0) {
		return (
			<section className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] px-4 py-3">
				<p className="text-xs text-[var(--color-muted)]">
					No trophies yet — opt into Fantasy Fams to compete.
				</p>
			</section>
		);
	}

	return (
		<section className="space-y-2">
			<h2 className="text-xs uppercase tracking-wider text-[var(--color-muted)]">
				Trophies
			</h2>
			<div className="flex flex-wrap gap-2">
				{list.map((t, i) => (
					<TrophyBadge key={`${t.type}-${t.season ?? i}`} trophy={t} />
				))}
			</div>
		</section>
	);
}

// --- helpers ---------------------------------------------------------------

/**
 * Strip an exchange prefix like "NASDAQ:AAPL" → "AAPL". The Fam composite
 * stores fully qualified symbols; the stock route + display label want the
 * bare ticker.
 */
function bareSymbol(s: string): string {
	const last = s.split(":").pop();
	return (last ?? s).toUpperCase();
}

function formatPctSigned(n: number): string {
	if (!Number.isFinite(n)) return "—";
	const sign = n > 0 ? "+" : "";
	return `${sign}${n.toFixed(2)}%`;
}

/**
 * Allocation-weighted average of per-symbol day-change percentages. Returns
 * null if we don't have a quote for at least one entry — the partial number
 * would be misleading. The Worker's quotes endpoint is best-effort
 * (per-symbol failures within a batch are just omitted), so we explicitly
 * check coverage.
 */
function computeWeightedDayChange(
	entries: [string, number][],
	quotesByKey: Record<string, Quote>,
): number | null {
	if (entries.length === 0) return null;
	let weight = 0;
	let weighted = 0;
	for (const [symbol, alloc] of entries) {
		// The batch keys come back keyed on the same input symbol the caller
		// passed (uppercased). Fall back to the bare ticker for safety.
		const q = quotesByKey[symbol] ?? quotesByKey[bareSymbol(symbol)];
		if (!q || !Number.isFinite(q.changePct)) return null;
		weight += alloc;
		weighted += alloc * q.changePct;
	}
	if (weight <= 0) return null;
	return weighted / weight;
}
